import React from 'react';
import Markdown from 'marked-react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import { Link, useParams } from 'react-router-dom';
import { Seo } from '../components/site/Seo';
import { useContentfulCollection } from '../hooks/useContentfulCollection';
import { NotFoundPage } from './NotFoundPage';

function slugify(value) {
  return (value || 'job')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function getJobSlug(job) {
  return job.slug || slugify(job.title);
}

function formatJobDate(value) {
  if (!value) {
    return '';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return '';
  }

  return parsedDate.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function getJobSummary(job) {
  return job.summary || job.description || '';
}

function getJobMetaItems(job) {
  return [
    job.team || job.department ? { label: 'Team', value: job.team || job.department } : null,
    job.location ? { label: 'Location', value: /^-?\d+\.\d+,\s*-?\d+\.\d+$/.test((job.location || '').trim()) ? 'Sheffield, UK' : job.location } : null,
    job.schedule || job.employmentType ? { label: 'Schedule', value: job.schedule || job.employmentType } : null,
    job.jobId ? { label: 'Job ID', value: job.jobId } : null,
    formatJobDate(job.publishedAt || job.date)
      ? { label: 'Posted', value: formatJobDate(job.publishedAt || job.date) }
      : null,
  ].filter(Boolean);
}

function getApplyHref(job) {
  const role = encodeURIComponent(job.title || getJobSlug(job));
  return `/careers?role=${role}#job-application`;
}

export function JobDetailPage() {
  const { slug } = useParams();
  const { items: jobs, isLoading } = useContentfulCollection('jobs', { pageSize: 50 });
  const job = jobs.find((item) => getJobSlug(item) === slug || item.slug === slug);

  if (!job && isLoading) {
    return <div className='page-shell loading-shell'>Loading job…</div>;
  }

  if (!job) {
    return <NotFoundPage />;
  }

  const summary = getJobSummary(job);
  const metaItems = getJobMetaItems(job);
  const expectations = job.expectations || [];
  const hasStructuredFallback = summary || expectations.length || job.callout;

  const richTextOptions = {
    renderMark: {
      [MARKS.BOLD]: (text) => <strong>{text}</strong>,
      [MARKS.ITALIC]: (text) => <em>{text}</em>,
      [MARKS.UNDERLINE]: (text) => <u>{text}</u>,
      [MARKS.CODE]: (text) => <code>{text}</code>,
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => <p>{children}</p>,
      [BLOCKS.HEADING_1]: (node, children) => <h1>{children}</h1>,
      [BLOCKS.HEADING_2]: (node, children) => <h2>{children}</h2>,
      [BLOCKS.HEADING_3]: (node, children) => <h3>{children}</h3>,
      [BLOCKS.HEADING_4]: (node, children) => <h4>{children}</h4>,
      [BLOCKS.HEADING_5]: (node, children) => <h5>{children}</h5>,
      [BLOCKS.HEADING_6]: (node, children) => <h6>{children}</h6>,
      [BLOCKS.UL_LIST]: (node, children) => <ul className='bullet-list'>{children}</ul>,
      [BLOCKS.OL_LIST]: (node, children) => <ol className='bullet-list'>{children}</ol>,
      [BLOCKS.LIST_ITEM]: (node, children) => <li>{children}</li>,
      [BLOCKS.QUOTE]: (node, children) => <blockquote className='detail-quote'>{children}</blockquote>,
      [BLOCKS.HR]: () => <hr />,
      [INLINES.HYPERLINK]: (node, children) => (
        <a href={node.data.uri} target='_blank' rel='noopener noreferrer'>{children}</a>
      ),
    },
  };

  return (
    <>
      <Seo title={`${job.title} | Jobs | Rivelin Robotics`} description={summary} />
      <article className='detail-shell job-detail-shell'>
        <Link className='inline-link back-link' to='/careers#open-roles'>
          ← Back to open roles
        </Link>
        <p className='section-label'>{job.team || job.department || 'Open role'}</p>
        <h1 className='detail-title'>{job.title}</h1>
        {summary ? <p className='detail-summary'>{summary}</p> : null}
        <div className='hero-actions job-detail-actions'>
          <Link className='primary-cta' to={getApplyHref(job)}>
            Apply for this role
          </Link>
          <Link className='secondary-cta' to='/careers'>
            Careers
          </Link>
        </div>

        {metaItems.length ? (
          <div className='detail-grid job-detail-meta-grid'>
            {metaItems.map((item) => (
              <section key={item.label} className='detail-panel job-detail-meta-card'>
                <p className='section-label'>{item.label}</p>
                <p>{item.value}</p>
              </section>
            ))}
          </div>
        ) : null}

        {job.jobDescriptionRich ? (
          <div className='detail-body markdown-body'>
            {documentToReactComponents(job.jobDescriptionRich, richTextOptions)}
          </div>
        ) : job.body ? (
          <div className='detail-body markdown-body'>
            <Markdown>{job.body}</Markdown>
          </div>
        ) : null}

        {!job.body && hasStructuredFallback ? (
          <>
            {expectations.length ? (
              <section className='detail-panel'>
                <h2>Role focus</h2>
                <ul className='bullet-list'>
                  {expectations.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>
            ) : null}
            {job.callout ? (
              <blockquote className='detail-quote job-detail-callout'>
                <p>{job.callout}</p>
              </blockquote>
            ) : null}
          </>
        ) : null}

        <div className='hero-actions job-detail-actions'>
          <Link className='primary-cta' to={getApplyHref(job)}>
            Apply for this role
          </Link>
        </div>
      </article>
    </>
  );
}
